import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)]">
      <Navbar />

      <div className="flex justify-center px-5 lg:py-12 py-8">
        <main className="max-w-[1280px] w-full">
          <button
            onClick={() => {
              if (window.history.length > 2) {
                navigate(-1);
              } else {
                navigate("/");
              }
            }}
            className="flex items-center gap-1.5 appearance-none border-none bg-transparent xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-semibold cursor-pointer md:mb-6 mb-4"
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 20 }} /> Back to Home
          </button>

          <header className="flex flex-col gap-2">
            <h1 className="xl:text-6xl md:text-5xl text-[40px] leading-[0.98] font-bold tracking-[-0.03em]">
              Privacy Policy
            </h1>
            <p className="text-[var(--color-primary)] xl:text-base md:text-sm text-xs font-bold">
              Last updated: August 7, 2026
            </p>
          </header>

          <div className="h-[1px] bg-[var(--color-border)] my-6" />

          <div className="flex flex-col">
            <Section number="1" title="Introduction">
              <p>Welcome to <strong>The FormGuru</strong>. We are committed to protecting your privacy and ensuring the security of your personal data.</p>
              <p>This Privacy Policy explains how we collect, use, disclose, store, and safeguard your personal information when you register for, access, or use our website, web application, mobile applications, APIs, and related horse race analytics and prediction services (collectively, the "Services").</p>
              <p>Please read this Privacy Policy carefully. By creating an account, accepting our Terms of Service, or using our Services, you acknowledge that you have read, understood, and agree to the collection and use of your information as described in this policy.</p>
              <p>If you do not agree with any terms of this Privacy Policy, please discontinue access and use of our Services immediately.</p>
            </Section>

            <Section number="2" title="Information We Collect">
              <p>We collect information from and about users through various interactions with our platform. The types of data collected depend on your subscription level, account settings, and features used.</p>

              <SubSection title="2.1 Information You Provide Directly" />
              <BulletList items={[
                "Account Registration & Contact Details: First name, last name, and email address.",
                "Authentication Credentials: Password hash (for email-based sign-up), verification One-Time Passwords (OTPs), and security reset tokens.",
                "Third-Party Identity / Single Sign-On (SSO): Unique provider user identifiers (e.g., Google ID and Apple ID) when you choose to register or log in using third-party social authentication.",
                "Consent & Legal Agreement Records: Confirmation of agreement to our Terms of Service, timestamps of consent, and account approval/verification timestamps.",
              ]} />

              <SubSection title="2.2 Payment and Billing Information" />
              <BulletList items={[
                "Subscription Tier & Preferences: Selected plan type (e.g., Free, Pro 4-Week, Pro Month, Pro Annual), billing intervals, auto-debit preferences, subscription status, and start/expiry dates.",
                "Transaction Metadata: Invoice numbers, transaction IDs, payment statuses (paid, pending, failed, refunded), payment currency (USD), payment method type (Card, Bank Debit, etc.), and timestamps.",
                "Payment Gateways: Payment card details and sensitive financial data are processed directly by our secure third-party payment gateway providers. We store payment reference IDs and invoice metadata, but we do not store full credit card numbers or security codes on our servers.",
              ]} />

              <SubSection title="2.3 Device & Technical Information" />
              <BulletList items={[
                "Push Notification & Device Identifiers: Push notification tokens (such as Firebase Cloud Messaging tokens), platform type (Web, Android, iOS), active status, and device association.",
                "System & Log Data: Internet Protocol (IP) address, browser type, operating system, timestamped access logs, system performance metrics, and application errors.",
              ]} />

              <SubSection title="2.4 Service Usage & Interaction Data" />
              <BulletList items={[
                "User Preferences & Interactions: In-app notification preferences, notification read history, race query parameters, dashboard interaction logs, and subscription limits usage.",
              ]} />
            </Section>

            <Section number="3" title="How We Use Your Information">
              <p>We process your personal information for the following specific purposes:</p>
              <BulletList items={[
                "Service Delivery & Provisioning: To create and manage your account, authenticate logins via email OTP or SSO (Google/Apple), process race analytics queries, and enforce tier-based feature access (e.g., race limits based on subscription status).",
                "Billing & Subscription Administration: To process payments, issue invoices, maintain transaction records, handle auto-debit renewals, and manage subscription state changes (activations, expirations, cancellations).",
                "Notifications & Alerts: To dispatch transactional notifications, system alerts, subscription updates, prediction summaries, and promotional messages to registered web and mobile devices.",
                "Platform Security & Fraud Prevention: To verify user identity via OTP, manage secure password resets (via temporary tokens with 5 to 15-minute expirations), protect against unauthorized access, and ensure platform integrity.",
                "Analytics & Service Improvement: To evaluate platform usage, monitor system stability, optimize user experience, and refine prediction data features.",
                "Legal & Regulatory Compliance: To maintain legal audit logs of terms acceptance, respond to lawful law enforcement requests, resolve disputes, and comply with tax and accounting rules.",
              ]} />
            </Section>

            <Section number="4" title="How We Share Your Data">
              <p>We do not sell, rent, or trade your personal information to third parties. We share information only under the following circumstances:</p>

              <SubSection title="4.1 Third-Party Service Providers" />
              <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-medium">We share necessary information with trusted vendor partners who provide infrastructure, processing, or application support services under strict confidentiality agreements:</p>
              <BulletList items={[
                "Authentication Providers: Google Sign-In and Apple Sign-In services for account verification.",
                "Push Notification Providers: Firebase / Google Cloud Messaging and Apple Push Notification service (APNs) for sending real-time alerts to registered device tokens.",
                "Payment Processors: Secure third-party payment gateways for processing credit cards, debit cards, and automated billing.",
                "Cloud & Hosting Infrastructure: Cloud server hostings and database providers hosting our backend services and analytical databases.",
              ]} />

              <SubSection title="4.2 Business Transfers" />
              <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-medium">In the event of a merger, acquisition, corporate reorganization, asset sale, or bankruptcy, your account and subscription information may be transferred as part of the business assets. You will be notified via email or a prominent notice on our website prior to any transfer.</p>

              <SubSection title="4.3 Legal & Compliance Disclosures" />
              <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-medium">We may disclose information if required to do so by applicable law, court order, subpoena, government regulation, or if we believe in good faith that such disclosure is necessary to:</p>
              <BulletList items={[
                "Comply with legal processes or legal obligations.",
                "Protect and defend our rights, property, or safety, or that of our users or the public.",
                "Enforce our Terms of Service or investigate suspected fraudulent or illegal activities.",
              ]} />
            </Section>

            <Section number="5" title="Data Retention and Security">
              <SubSection title="5.1 Retention" />
              <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-medium">We retain your personal data for as long as your account remains active or as needed to provide you with the Services. We will also retain and use your information to comply with legal, accounting, or regulatory requirements:</p>
              <BulletList items={[
                "Account Records: Retained until account closure or request for deletion, subject to legal audit obligations.",
                "OTP & Password Tokens: Automatically expire and cleared within 5 to 15 minutes of generation.",
                "Financial & Invoice Data: Retained for the statutory tax and accounting retention period required by law.",
                "Device Tokens: Kept while active; deactivated or deleted upon account deletion or device unregistration.",
              ]} />

              <SubSection title="5.2 Security Safeguards" />
              <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-medium">We implement industry-standard administrative, physical, and technical security controls to safeguard your data, including:</p>
              <BulletList items={[
                "Transport Layer Security (TLS/SSL) encryption for data in transit across web and mobile clients.",
                "Cryptographic hashing for user passwords.",
                "Short-lived OTPs and authorization tokens to secure sensitive actions (login, registration, password resets).",
                "Restricted backend database access controls based on the principle of least privilege.",
              ]} />
              <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-medium">However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your personal information, we cannot guarantee absolute security.</p>
            </Section>

            <Section number="6" title="Cookies and Tracking Technologies">
              <p>Our web application uses essential storage mechanisms (such as HTTP cookies, local storage, and session storage) to maintain user authentication states, preserve user interface settings, and store security tokens.</p>
              <p>You can set your browser to refuse all or some browser cookies, or to alert you when websites set or access cookies. However, disabling cookies or local storage may render key parts of our authenticated dashboard and prediction services unusable.</p>
            </Section>

            <Section number="7" title="Your Rights and Choices">
              <p>Depending on your jurisdiction (e.g., GDPR, CCPA/CPRA, or local privacy regulations), you have specific rights regarding your personal information:</p>
              <BulletList items={[
                "Access & Portability: Request a copy of the personal information we hold about you.",
                "Rectification: Request correction of inaccurate or incomplete personal information.",
                "Deletion (\"Right to be Forgotten\"): Request deletion of your personal account and associated data, subject to legal retention obligations.",
                "Opt-Out of Notifications: Turn off push notifications via your device settings or update your notification preferences within the app dashboard.",
                "Subscription Management: Cancel auto-debit renewals, change subscription tiers, or opt out of recurring plans via your account dashboard settings.",
              ]} />
              <p>To exercise any of these rights, please contact us at{' '}
                <a
                  href="mailto:service@formguru.com.au"
                  className="text-[var(--color-primary)] font-semibold"
                >
                  service@formguru.com.au
                </a>.
              </p>
            </Section>

            <Section number="8" title="Age Restrictions & Age Requirements">
              <p>Our Services provide sports analytics and predictive data intended solely for users who are at least 18 years of age (or the legal age of majority in your jurisdiction). We do not knowingly collect personal data from individuals under 18. If we become aware that a user under the legal age has registered an account, we will take steps to terminate the account and delete their information promptly.</p>
            </Section>

            <Section number="9" title="Third-Party Links & Services">
              <p>Our platform may contain links to external third-party websites or services. We are not responsible for the privacy practices or content of third-party platforms. We encourage you to review the privacy policies of any third-party site you visit.</p>
            </Section>

            <Section number="10" title="Updates to This Privacy Policy">
              <p>We may update this Privacy Policy periodically to reflect changes in our legal obligations, business operations, or service features. When updates occur, we will revise the "Last Updated" date at the top of this document. For significant updates, we will notify you by email or via a prominent notification banner on the platform before the changes take effect.</p>
            </Section>

            <Section number="11" showBorder={false} title="Contact Us">
              <p>If you or your legal/compliance team have questions, concerns, or requests regarding this Privacy Policy or our data handling practices, please contact us:</p>
              <p>Company Name:{' '}
                <span
                  className="text-[var(--color-primary)] font-semibold"
                >
                  The FormGuru
                </span>
              </p>
              <p>Email:{' '}
                <a
                  href="mailto:service@formguru.com.au"
                  className="text-[var(--color-primary)] font-semibold"
                >
                  service@formguru.com.au
                </a>
              </p>
              <p>Website:{' '}
                <a
                  href="https://formguru.com.au"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--color-primary)] font-semibold"
                >
                  https://formguru.com.au
                </a>
              </p>
            </Section>
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
}

function Section({ number, showBorder = true, title, children }) {
  return (
    <>
      <div className="flex flex-col gap-2">
        <h2 className="xl:text-xl md:text-lg text-base font-bold tracking-[-0.01em] flex items-center gap-2">
          <span className="tabular">
            {number}.
          </span>
          {title}
        </h2>
        <div className="flex flex-col gap-2 xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-medium">
          {children}
        </div>
      </div>

      {showBorder && <div className="h-[1px] bg-[var(--color-border)] my-6" />}
    </>
  );
}

function SubSection({ title }) {
  return (
    <h3 className="xl:text-lg md:text-base text-sm font-semibold tracking-[-0.01em] mt-2">
      {title}
    </h3>
  );
}

function BulletList({ items }) {
  return (
    <ul className="flex pl-3 flex-col gap-1">
      {items.map((item, i) => (
        <li key={i} className="flex items-center gap-3">
          <span className="text-[var(--color-primary)] md:text-xl text-lg flex-shrink-0">&bull;</span>
          <span className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-medium">{item}</span>
        </li>
      ))}
    </ul>
  );
}
